import React from 'react';
import { PubHeader } from '../components/PubHeader';
import { AuthorBlock } from '../components/AuthorBlock';
import { ImageBlock } from '../components/ImageBlock';
import { Divider } from '../components/Divider';
import { PullQuote } from '../components/PullQuote';
import { Timeline } from '../components/Timeline';
import { HerbGrid } from '../components/HerbGrid';
import { TestimonialBlock } from '../components/TestimonialBlock';
import { ProductSection } from '../components/ProductSection';
export function ArticlePage() {
  const timelineItems = [{
    week: 'Week 1-2',
    text: 'Not much. I was honestly expecting nothing. I woke up damp a couple of nights instead of completely soaked. I thought it might be coincidence or the weather changing.'
  }, {
    week: 'Week 3',
    text: "I slept until 4:30am without waking up. I know that doesn't sound remarkable but I had not done that in eleven months. I lay there for a few minutes just to make sure I was really awake. I was. The towel was barely damp."
  }, {
    week: 'Week 4-5',
    text: "The hot flashes in the evenings started coming less frequently. Not gone - but less. I stopped setting the bedroom to 64 degrees. My husband stopped sleeping in a hoodie. We laughed about it, which felt significant because we hadn't laughed much in a year."
  }, {
    week: 'Week 6',
    text: "I put the towel away. I haven't needed it since."
  }, {
    week: 'Week 8',
    text: 'My husband said something to me one evening. He said - "I feel like I have my wife back." I cried. Not from exhaustion. From relief.'
  }];
  const herbItems = [{
    name: 'Black Cohosh 160mg',
    description: 'Clinically studied for hot flash reduction and hypothalamic temperature regulation. One of the most researched natural compounds for menopausal symptoms.'
  }, {
    name: 'Sage Leaf 200mg',
    description: 'Shown in studies to reduce night sweat frequency and severity. Specifically supports overnight thermoregulation - the mechanism behind 3am drenching.'
  }, {
    name: 'Chasteberry 50mg',
    description: 'Targets progesterone balance directly - addressing the mood collapse and brain fog that most women describe as "not feeling like myself."'
  }, {
    name: 'Dong Quai 150mg',
    description: 'The foundational herb for female hormonal health in Chinese medicine for centuries. Supports sleep quality and blood nourishment at the root level.'
  }];
  const testimonials = [{
    quote: "I actually slept through the night for the first time in two years. I didn't know what to do with myself when I woke up and it was morning.",
    author: '— Sarah K., 52  ·  Verified Customer'
  }, {
    quote: 'The rage just... stopped. My husband noticed before I did. I felt like myself again for the first time in over a year.',
    author: '— Deborah M., 49  ·  Verified Customer'
  }, {
    quote: "I put the towel away after week six. I cried when I realised I hadn't needed it. Sounds small. It wasn't.",
    author: '— Michelle T., 51  ·  Verified Customer'
  }];
  return <div className="w-full bg-white text-[#111111] font-lora text-[19px] leading-[1.8]" data-id="element-46">
      <PubHeader data-id="element-47" />

      <article className="max-w-[660px] mx-auto pt-12 px-6 pb-20 max-[600px]:pt-9 max-[600px]:px-5 max-[600px]:pb-[60px]" data-id="element-48">
        <span className="font-inter text-[11px] tracking-[0.18em] uppercase text-teal font-semibold mb-[18px] block fade-in fade-in-1" data-id="element-49">
          Perimenopause &amp; Menopause
        </span>

        <h1 className="font-lora text-[clamp(26px,5vw,40px)] font-bold leading-[1.18] text-black mb-[22px] tracking-[-0.01em] fade-in fade-in-2" data-id="element-50">
          I Saw Four Doctors in 14 Months. Not One of Them Said Perimenopause
          First. Then I Found Out What Was Actually Happening to My Body.
        </h1>

        <p className="font-lora text-[clamp(16px,2.5vw,19px)] font-normal italic text-[#222222] leading-[1.6] mb-6 pb-6 border-b border-border fade-in fade-in-3" data-id="element-51">
          I used to dread going to sleep. Now I wake up before my alarm and
          actually feel rested. Here's the one thing that changed everything.
        </p>

        <AuthorBlock imageSrc="https://cdn.shopify.com/s/files/1/0760/8251/2099/files/Untitled_design_23_7e23c162-5cbe-4087-8451-7718c34de7d7.png?v=1773359917" name="Karen Mitchell, 51 - Phoenix, Arizona" meta="Updated March 2026" className="fade-in fade-in-4" data-id="element-52" />

        <ImageBlock src="https://cdn.shopify.com/s/files/1/0760/8251/2099/files/ChatGPT_Image_Mar_12_2026_09_29_33_PM.png?v=1773313687" alt="Woman sitting on edge of bed exhausted early morning" caption="This was me. Every single morning for eleven months. Sitting on the edge of the bed wondering how I was going to get through another day running on no real sleep." data-id="element-53" />

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-54">
          <p data-id="element-55">I want to be honest with you about something.</p>
          <p data-id="element-56">I almost didn't write this.</p>
          <p data-id="element-57">
            Because I know how it sounds. Another woman on the internet telling
            you she found something that fixed her menopause symptoms. I would
            have scrolled past this six months ago.
          </p>
          <p data-id="element-58">
            But what happened to me is so different from everything I'd tried
            before that I feel like I have to share it. Especially because the
            explanation nobody gave me - the real explanation for what was
            happening inside my body - changed everything.
          </p>
          <p data-id="element-59">So here's my story. All of it.</p>
        </div>

        <Divider data-id="element-60" />

        <h2 className="font-lora text-[clamp(20px,3vw,26px)] font-bold text-black mt-9 mb-[18px] leading-[1.3]" data-id="element-61">
          The year I stopped recognising myself.
        </h2>

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-62">
          <p data-id="element-63">It started the summer I turned 50.</p>
          <p data-id="element-64">
            I'd always been a good sleeper. Eight hours. Never needed an alarm.
            My husband used to joke that I could sleep through anything.
          </p>
          <p data-id="element-65">
            Then one night in July I woke up at 2:47am completely soaked.
            Sheets, pillow, t-shirt - everything. I thought I was sick. I
            changed everything, got back into bed, and lay there staring at the
            ceiling until 5am.
          </p>
          <p data-id="element-66">I told myself it was a one-off.</p>
          <p data-id="element-67">It wasn't.</p>
          <p data-id="element-68">
            By September I was putting a fresh towel down on my side of the bed
            every single night. I stopped wearing nice pyjamas. I set the
            bedroom to 64 degrees in the Arizona summer - which meant my husband
            was sleeping in a hoodie - and I was still waking up drenched.
          </p>
          <p data-id="element-69">But the sleep wasn't even the worst part.</p>
          <p data-id="element-70">The worst part was the rage.</p>
          <p data-id="element-71">
            I sat in my car in my own driveway for 20 minutes one Tuesday
            evening because my husband had left a cabinet door open in the
            kitchen.{' '}
            <strong className="text-black font-bold" data-id="element-72">One cabinet door.</strong>{' '}
            And I couldn't go back inside yet.
          </p>
          <p data-id="element-73">
            I love this man. I have loved him for 24 years. And I couldn't be
            near him because of a cabinet door.
          </p>
          <p data-id="element-74">
            That night I cried in the car and thought - this isn't me. Something
            is genuinely wrong.
          </p>
        </div>

        <Divider data-id="element-75" />

        <h2 className="font-lora text-[clamp(20px,3vw,26px)] font-bold text-black mt-9 mb-[18px] leading-[1.3]" data-id="element-76">
          Four doctors. Fourteen months. Not one of them said perimenopause
          first.
        </h2>

        <ImageBlock src="https://cdn.shopify.com/s/files/1/0760/8251/2099/files/ChatGPT_Image_Mar_12_2026_09_29_36_PM.png?v=1773313686" alt="Empty doctor's office cold and institutional" caption="I sat in rooms like this fourteen times over fourteen months. Every single doctor looked at my bloodwork and said some version of the same thing." data-id="element-77" />

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-78">
          <p data-id="element-79">The first doctor suggested antidepressants.</p>
          <p data-id="element-80">
            I'm not depressed. I'm exhausted and I'm overheating and I feel like
            a stranger in my own body. Those are not the same thing.
          </p>
          <p data-id="element-81">
            The second told me to manage my stress better. The third said my
            bloodwork was completely normal. The fourth - a woman, which I
            thought would make a difference - said this was just part of getting
            older and I needed to adjust my expectations.
          </p>
          <p data-id="element-82">
            <em className="italic" data-id="element-83">Adjust my expectations.</em>
          </p>
          <p data-id="element-84">
            I drove home from that last appointment and sat in the carpark for a
            while. Not crying. Just done. Done with the system. Done with being
            told that feeling like I was disappearing inside my own life was
            normal and expected and something I should simply accept.
          </p>
        </div>

        <PullQuote data-id="element-85">
          "Your bloodwork is normal." Four words. Fourteen months. Not one
          answer.
        </PullQuote>

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-86">
          <p data-id="element-87">
            I went home that night and started reading everything I could find.
            Forums. Studies. Reddit threads at 2am when I couldn't sleep anyway.
            That's when I first came across something I'd never heard before.
          </p>
        </div>

        <Divider data-id="element-88" />

        <h2 className="font-lora text-[clamp(20px,3vw,26px)] font-bold text-black mt-9 mb-[18px] leading-[1.3]" data-id="element-89">
          The explanation that finally made sense of everything.
        </h2>

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-90">
          <p data-id="element-91">
            In Chinese medicine, menopause isn't treated as an estrogen problem.
          </p>
          <p data-id="element-92">
            It's understood as something called{' '}
            <em className="italic" data-id="element-93">yin decline.</em>
          </p>
          <p data-id="element-94">
            Yin is the cooling, nourishing, fluid energy that keeps your body's
            internal systems in balance. When yin declines - which happens
            naturally during perimenopause - your body loses its ability to
            regulate its own temperature. Its ability to anchor your mood
            weakens. Its ability to sustain deep restorative sleep disappears.
          </p>
          <p data-id="element-95">That's why the heat rises in your chest for no reason at 11pm.</p>
          <p data-id="element-96">
            That's why you wake up soaked at 3am even when the room is cold.
          </p>
          <p data-id="element-97">
            That's why the rage comes from nowhere and disappears just as fast -
            leaving you feeling guilty and confused and less like yourself every
            single time.
          </p>
          <p data-id="element-98">It's not low estrogen. It's not depression. It's not stress.</p>
          <p data-id="element-99">
            <strong className="text-black font-bold" data-id="element-100">
              It's systemic imbalance. And estrogen replacement doesn't fix it.
            </strong>
          </p>
          <p data-id="element-101">
            That's why so many women on HRT still don't feel like themselves.
            They're replacing one hormone in a system that needs something
            completely different.
          </p>
        </div>

        <ImageBlock src="https://cdn.shopify.com/s/files/1/0760/8251/2099/files/ChatGPT_Image_Mar_12_2026_09_31_48_PM.png?v=1773313688" alt="Dried medicinal herbs on wooden surface" caption="Chinese medicine has been using specific herbs to nourish yin and restore female hormonal balance for over 2,000 years. The research backing them is now significant." data-id="element-102" />

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-103">
          <p data-id="element-104">
            Chinese medicine has been addressing this for over 2,000 years using
            specific herbs that nourish yin, replenish fluids, and restore the
            body's ability to regulate itself from within.
          </p>
          <p data-id="element-105">Not from the outside. Not by suppressing symptoms.</p>
          <p data-id="element-106">By rebuilding the system that's breaking down.</p>
        </div>

        <Divider data-id="element-107" />

        <h2 className="font-lora text-[clamp(20px,3vw,26px)] font-bold text-black mt-9 mb-[18px] leading-[1.3]" data-id="element-108">
          I was sceptical. I want to be upfront about that.
        </h2>

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-109">
          <p data-id="element-110">
            I've tried things before. Magnesium. Evening primrose oil. Black
            cohosh on its own. A cooling weighted blanket that cost me $180 and
            did absolutely nothing.
          </p>
          <p data-id="element-111">
            So when I came across Lumé Vitality - a 7-herb formula specifically
            designed around this Chinese medicine framework - my first instinct
            was to scroll past it.
          </p>
          <p data-id="element-112">
            But I kept reading about what was actually in it. And the specific
            combination of herbs - each one targeting a different part of what I
            now understood was happening - made me stop.
          </p>
        </div>

        <HerbGrid items={herbItems} data-id="element-113" />

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-114">
          <p data-id="element-115">
            It wasn't one herb doing one thing. It was seven herbs working
            together as a complete system - the same way Chinese medicine has
            always understood the female body needs to be supported.
          </p>
          <p data-id="element-116">
            They had a 60-day money back guarantee. I'd already spent $400 on a
            doctor who told me to adjust my expectations.
          </p>
          <p data-id="element-117">I ordered it that night.</p>
        </div>

        <Divider data-id="element-118" />

        <h2 className="font-lora text-[clamp(20px,3vw,26px)] font-bold text-black mt-9 mb-[18px] leading-[1.3]" data-id="element-119">
          What happened over the next eight weeks.
        </h2>

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-120">
          <p data-id="element-121">
            I'm going to be specific here because vague testimonials drove me
            crazy when I was researching this. So here's exactly what I noticed
            and when.
          </p>
        </div>

        <Timeline items={timelineItems} data-id="element-122" />

        <div className="space-y-6 text-[clamp(17px,2.2vw,19px)] text-[#111111] font-normal leading-[1.8]" data-id="element-123">
          <p data-id="element-124">I'm still on it. I have no intention of stopping.</p>
        </div>

        <ImageBlock src="https://cdn.shopify.com/s/files/1/0760/8251/2099/files/ChatGPT_Image_Mar_12_2026_09_32_24_PM.png?v=1773313687" alt="Woman holding capsule in warm kitchen calm expression" caption="Eight weeks in. This is what relief actually looks like - not excitement, not performance. Just quietly feeling like yourself again." data-id="element-125" />

        <Divider data-id="element-126" />

        <h2 className="font-lora text-[clamp(20px,3vw,26px)] font-bold text-black mt-9 mb-[18px] leading-[1.3]" data-id="element-127">
          I'm not the only one.
        </h2>

        <TestimonialBlock testimonials={testimonials} data-id="element-128" />

        <ProductSection data-id="element-129" />

        <p className="mt-[60px] pt-6 border-t border-border font-inter text-[11px] text-[#777777] leading-[1.65] italic" data-id="element-130">
          Results may vary. Individual experiences will differ. These statements
          have not been evaluated by the Food and Drug Administration. This
          product is not intended to diagnose, treat, cure, or prevent any
          disease. Karen's results were achieved over 8 weeks of consistent use.
          The testimonials featured may have used more than one product or
          extended the usage period to achieve their results. Sponsored by Lumé
          Vitality.
        </p>
      </article>
    </div>;
}